"use client";

import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { useTranslations } from "next-intl";
import { useAuth } from "@/contexts/AuthContext";
import type { ProyectosPropios } from "@/types/models/propios";
import { propiosService } from "@/lib/services/propios.service";

export interface ProyectosPropiosData {
  proyectosPropios: ProyectosPropios[];
  isFetching: boolean;
  error: unknown;
  refetchAll: () => Promise<void>;
  deleteProyectoPropio: (id: number) => Promise<void>;
}

export function useProyectosPropiosData(): ProyectosPropiosData {
  const { isLoggedIn, currentTenantId, userLang } = useAuth();
  const t = useTranslations();

  const [proyectosPropios, setProyectosPropios] = useState<ProyectosPropios[]>(
    []
  );
  const [isFetching, setIsFetching] = useState(false);
  const [error, setError] = useState<unknown>(null);

  const canFetch = isLoggedIn && !!currentTenantId;
  const tenantId = currentTenantId ? Number(currentTenantId) : null;

  const toastMessages = useMemo(
    () => ({
      fetchErrorTitle: t("toast.proyectos-propios.fetch-error-title"),
      fetchErrorDescription: t("toast.proyectos-propios.fetch-error-desc"),
      successTitle: t("toast.success-title"),
      errorTitle: t("toast.error-title"),
      deleteSuccess: t("messages.deleteSuccess"),
      deleteError: t("messages.deleteError"),
    }),
    [t]
  );

  const showFetchErrorToast = useCallback(() => {
    toast.error(toastMessages.fetchErrorTitle, {
      description: toastMessages.fetchErrorDescription,
      descriptionClassName: "!text-gray-800 dark:!text-gray-200",
    });
  }, [toastMessages]);

  const showDeleteSuccessToast = useCallback(() => {
    toast.success(toastMessages.successTitle, {
      description: toastMessages.deleteSuccess,
      descriptionClassName: "!text-gray-800 dark:!text-gray-200",
    });
  }, [toastMessages]);

  const showDeleteErrorToast = useCallback(() => {
    toast.error(toastMessages.errorTitle, {
      description: toastMessages.deleteError,
      descriptionClassName: "!text-gray-800 dark:!text-gray-200",
    });
  }, [toastMessages]);

  const refetchAll = useCallback(async () => {
    if (!canFetch || !tenantId) return;

    setIsFetching(true);
    setError(null);

    try {
      const data = await propiosService.fetchProyectosPropiosByTenant({
        tenantId,
        userLang,
      });

      setProyectosPropios(data);
    } catch (err) {
      setError(err);
      showFetchErrorToast();
    } finally {
      setIsFetching(false);
    }
  }, [canFetch, tenantId, userLang, showFetchErrorToast]);

  const deleteProyectoPropio = useCallback(
    async (id: number) => {
      if (!canFetch) return;

      try {
        await propiosService.deleteProyectoPropio(id);
        await refetchAll();
        showDeleteSuccessToast();
      } catch (err) {
        showDeleteErrorToast();
        throw err;
      }
    },
    [canFetch, refetchAll, showDeleteSuccessToast, showDeleteErrorToast]
  );

  useEffect(() => {
    if (!canFetch || !tenantId) {
      setProyectosPropios([]);
      setError(null);
      return;
    }

    void refetchAll();
  }, [canFetch, tenantId, refetchAll]);

  return useMemo(
    () => ({
      proyectosPropios,
      isFetching,
      error,
      refetchAll,
      deleteProyectoPropio,
    }),
    [
      proyectosPropios,
      isFetching,
      error,
      refetchAll,
      deleteProyectoPropio,
    ]
  );
}
